<template>
  <div>
    <Header />
  <v-container fluid>
    <v-card>
      <v-card-title>
        <span class="headline">Benutzerverwaltung</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Neuer Benutzer
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filters -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Suchen"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-checkbox
              v-model="showOnlyActive"
              label="Nur aktive Benutzer anzeigen"
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>

        <!-- Users table -->
        <v-data-table
          :headers="headers"
          :items="filteredUsers"
          :items-per-page="10"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:item.active="{ item }">
            <v-chip
              :color="item.active ? 'green' : 'red'"
              text-color="white"
              small
            >
              {{ item.active ? 'Aktiv' : 'Inaktiv' }}
            </v-chip>
          </template>

          <template v-slot:item.role="{ item }">
            <v-chip
              :color="getRoleColor(item.role)"
              text-color="white"
              small
            >
              {{ item.role }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon
              small
              class="mr-2"
              @click="editUser(item)"
              color="primary"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              @click="confirmDelete(item)"
              color="error"
            >
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ editMode ? 'Benutzer bearbeiten' : 'Neuer Benutzer' }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedUser.user"
                  label="Benutzername"
                  :rules="[rules.required]"
                  :disabled="editMode"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedUser.password"
                  label="Passwort"
                  type="password"
                  :rules="editMode ? [] : [rules.required]"
                  :hint="editMode ? 'Leer lassen um Passwort beizubehalten' : ''"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedUser.role"
                  :items="roles"
                  label="Rolle"
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedUser.typ"
                  :items="['Mensch', 'Maschine']"
                  label="Typ"
                  clearable
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedUser.ma_typ"
                  :items="['intern', 'extern']"
                  label="Mitarbeitertyp"
                  clearable
                  :disabled="editedUser.typ === 'Maschine'"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedUser.department"
                  :items="['Produktion', 'Buero']"
                  label="Abteilung"
                  clearable
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-switch
                  v-model="editedUser.active"
                  label="Aktiv"
                  :true-value="1"
                  :false-value="0"
                ></v-switch>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialog">Abbrechen</v-btn>
          <v-btn color="primary" @click="saveUser" :loading="saving">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Benutzer löschen?</v-card-title>
        <v-card-text>
          Möchten Sie den Benutzer "{{ userToDelete?.user }}" wirklich löschen?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Abbrechen</v-btn>
          <v-btn color="error" @click="deleteUser" :loading="deleting">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</div>
</template>

<script>
import AuthService from '@/services/AuthService'
import Header from './header'
export default {
  name: 'UserManagement',
  components: {
    Header
  },
  data() {
    return {
      users: [],
      loading: false,
      dialog: false,
      deleteDialog: false,
      editMode: false,
      saving: false,
      deleting: false,
      search: '',
      showOnlyActive: true, // Standardmäßig nur aktive anzeigen
      userToDelete: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      headers: [
        { text: 'ID', value: 'id', align: 'start' },
        { text: 'Benutzername', value: 'user' },
        { text: 'Rolle', value: 'role' },
        { text: 'Typ', value: 'typ' },
        { text: 'MA-Typ', value: 'ma_typ' },
        { text: 'Abteilung', value: 'department' },
        { text: 'Status', value: 'active' },
        { text: 'Aktionen', value: 'actions', sortable: false }
      ],
      editedUser: {
        user: '',
        password: '',
        role: 'worker',
        typ: null,
        ma_typ: null,
        department: null,
        active: 1
      },
      defaultUser: {
        user: '',
        password: '',
        role: 'worker',
        typ: null,
        ma_typ: null,
        department: null,
        active: 1
      },
      roles: ['admin', 'worker', 'minijob', 'support'],
      rules: {
        required: value => !!value || 'Pflichtfeld'
      }
    }
  },
  computed: {
    filteredUsers() {
      let filtered = this.users
      
      // Filter by active status
      if (this.showOnlyActive) {
        filtered = filtered.filter(user => user.active === 1)
      }
      
      // Filter by search term
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        filtered = filtered.filter(user => {
          return user.user.toLowerCase().includes(searchLower) ||
                 user.role?.toLowerCase().includes(searchLower) ||
                 user.department?.toLowerCase().includes(searchLower) ||
                 user.typ?.toLowerCase().includes(searchLower)
        })
      }
      
      return filtered
    }
  },
  mounted() {
    this.loadUsers()
  },
  methods: {
    async loadUsers() {
      this.loading = true
      try {
        const response = await AuthService.getAllUsers()
        this.users = response.data
      } catch (error) {
        console.error('Error loading users:', error)
        this.showSnackbar('Fehler beim Laden der Benutzer', 'error')
      } finally {
        this.loading = false
      }
    },

    openCreateDialog() {
      this.editMode = false
      this.editedUser = Object.assign({}, this.defaultUser)
      this.dialog = true
    },

    editUser(user) {
      this.editMode = true
      this.editedUser = Object.assign({}, user)
      // Don't copy password for security
      this.editedUser.password = ''
      this.dialog = true
    },

    closeDialog() {
      this.dialog = false
      setTimeout(() => {
        this.editedUser = Object.assign({}, this.defaultUser)
      }, 300)
    },

    async saveUser() {
      // Validate required fields
      if (!this.editedUser.user || (!this.editMode && !this.editedUser.password)) {
        this.showSnackbar('Bitte alle Pflichtfelder ausfüllen', 'error')
        return
      }

      this.saving = true
      try {
        if (this.editMode) {
          // Update existing user
          const updateData = { ...this.editedUser }
          // Remove password if empty (keep existing)
          if (!updateData.password) {
            delete updateData.password
          }
          
          await AuthService.updateUser(this.editedUser.id, updateData)
          this.showSnackbar('Benutzer erfolgreich aktualisiert', 'success')
        } else {
          // Create new user
          await AuthService.createUser(this.editedUser)
          this.showSnackbar('Benutzer erfolgreich erstellt', 'success')
        }
        
        this.closeDialog()
        this.loadUsers()
      } catch (error) {
        console.error('Error saving user:', error)
        const errorMsg = error.response?.data?.error || 'Fehler beim Speichern'
        this.showSnackbar(errorMsg, 'error')
      } finally {
        this.saving = false
      }
    },

    confirmDelete(user) {
      this.userToDelete = user
      this.deleteDialog = true
    },

    async deleteUser() {
      if (!this.userToDelete) return
      
      this.deleting = true
      try {
        await AuthService.deleteUser(this.userToDelete.id)
        this.showSnackbar('Benutzer erfolgreich gelöscht', 'success')
        this.deleteDialog = false
        this.loadUsers()
      } catch (error) {
        console.error('Error deleting user:', error)
        const errorMsg = error.response?.data?.error || 'Fehler beim Löschen'
        this.showSnackbar(errorMsg, 'error')
      } finally {
        this.deleting = false
        this.userToDelete = null
      }
    },

    getRoleColor(role) {
      const colors = {
        admin: 'red',
        worker: 'blue',
        minijob: 'green',
        support: 'orange'
      }
      return colors[role] || 'grey'
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}
</style>